import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { useACPP } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  MetricCard, Grid4, TableChip, BtnRow, BtnPrimary, BtnSecondary, BtnLight,
  InputLabel, FormInput, FormSelect, HelpText, EmptyState, TableWrap, Th, Td,
} from "../components/ui";
import ActionIconButton from "../../../Components/ActionIconButton";

function mkId() { return `r_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`; }

function OwnershipRow({
  row,
  gradeOptions,
  teacherOptions,
  hodOptions,
  getSubjectsForGrade,
  getSubjectMeta,
  onUpdate,
  onDelete,
  readOnly = false,
}) {
  const upd = (f, v) => onUpdate(row.id, f, v);
  const subjects = getSubjectsForGrade(row.grade);
  const meta = getSubjectMeta(row.grade, row.subject);

  return (
    <tr>
      <Td>{row.rowNum}</Td>
      <Td>
        <FormSelect value={row.grade} onChange={(e) => { upd("grade", e.target.value); upd("section", ""); upd("subject", ""); }} disabled={readOnly}>
          <option value="">Select grade</option>
          {gradeOptions.map((g) => <option key={g} value={g}>{g}</option>)}
        </FormSelect>
      </Td>
      <Td>
        <FormInput
          value={row.section}
          disabled={readOnly}
          onChange={(e) => upd("section", e.target.value)}
          placeholder="e.g. A, B, Red, Lotus"
        />
      </Td>
      <Td>
        <FormSelect value={row.subject} onChange={(e) => upd("subject", e.target.value)} disabled={readOnly}>
          <option value="">{subjects.length ? "Select subject" : "No subjects — complete Module 4"}</option>
          {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
        </FormSelect>
      </Td>
      <Td>
        <FormInput
          value={row.department_name}
          disabled={readOnly}
          onChange={(e) => upd("department_name", e.target.value)}
          placeholder="e.g. Science Department"
        />
      </Td>
      <Td>
        <FormSelect
          value={row.teacher_id}
          disabled={readOnly}
          onChange={(e) => {
            const id = e.target.value;
            const selected = teacherOptions.find((t) => t.id === id);
            upd("teacher_id", id);
            upd("teacher_name", selected?.name || "");
            upd("teachers", selected?.name ? [selected.name] : []);
          }}
        >
          <option value="">Select teacher</option>
          {teacherOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </FormSelect>
      </Td>
      <Td>
        <FormSelect
          value={row.hod_id}
          disabled={readOnly}
          onChange={(e) => {
            const id = e.target.value;
            const selected = hodOptions.find((h) => h.id === id);
            upd("hod_id", id);
            upd("hod_name", selected?.name || "");
            upd("hods", selected?.name ? [selected.name] : []);
          }}
        >
          <option value="">Select HOD</option>
          {hodOptions.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </FormSelect>
      </Td>
      <Td>
        <div className="flex flex-wrap gap-1">
          {meta.categories.length
            ? meta.categories.map((c) => <TableChip key={c} color={c === "Academic" ? "orange" : c === "Co-Curricular" ? "green" : "purple"}>{c}</TableChip>)
            : <span className="text-[11px] text-[#9ca3af]">—</span>}
        </div>
      </Td>
      <Td>
        <div className="flex flex-wrap gap-1">
          {meta.types.length
            ? meta.types.map((t) => <TableChip key={t}>{t}</TableChip>)
            : <span className="text-[11px] text-[#9ca3af]">—</span>}
        </div>
      </Td>
      <Td><ActionIconButton icon={Trash2} tone="delete" title="Delete" onClick={() => onDelete(row.id)} disabled={readOnly} /></Td>
    </tr>
  );
}

export default function Module5OwnershipMapping() {
  const {
    selectedGrades, subjectMappings,
    module5OwnershipMappings,
    module5Loading, module5LoadError,
    module5TeacherOptions, module5HodOptions, module5StaffLoading, module5StaffLoadError,
    module5ProvisionLoading, module5ProvisionError,
    canEditModules1to5,
    loadModule5AcademicOwnership, loadModule5StaffOptions,
    saveModule5AcademicOwnership, resetModule5AcademicOwnership, provisionModule5ToSchool,
    getSubjectsForGrade, getSubjectMeta,
  } = useACPP();

  const [ownershipRows, setOwnershipRows] = useState([]);
  const [provisionResult, setProvisionResult] = useState(null);

  const [filterGrade, setFilterGrade] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterTeacher, setFilterTeacher] = useState("");
  const [filterHod, setFilterHod] = useState("");

  const gradeOptions = selectedGrades;
  const hasSelectedGrades = gradeOptions.length > 0;

  const dedupe = useCallback((items) => {
    const seen = new Set();
    const out = [];
    (items || []).forEach((item) => {
      const clean = String(item ?? "").trim();
      if (clean && !seen.has(clean)) { seen.add(clean); out.push(clean); }
    });
    return out;
  }, []);

  const normalizeOwnershipMappings = useCallback((items) => {
    return (items || []).map((item, index) => {
      const grade = String(item?.grade ?? "").trim();
      const section = String(item?.section ?? "").trim();
      const subject = String(item?.subject ?? "").trim();
      const uid = String(item?.uid ?? "").trim() || `${grade}__${section}__${subject}__${index + 1}`;
      const teacher_name = String(item?.teacher_name || (item?.teachers || [])[0] || "").trim();
      const hod_name = String(item?.hod_name || (item?.hods || [])[0] || "").trim();
      return {
        uid,
        grade,
        section,
        subject,
        department_name: String(item?.department_name || "").trim(),
        teacher_id: String(item?.teacher_id || "").trim(),
        teacher_name,
        hod_id: String(item?.hod_id || "").trim(),
        hod_name,
        categories: dedupe(item?.categories),
        types: dedupe(item?.types),
        teachers: teacher_name ? [teacher_name] : [],
        hods: hod_name ? [hod_name] : [],
      };
    });
  }, [dedupe]);

  const ownershipMappingToRow = useCallback((mapping, index) => ({
    id: mkId(),
    uid: mapping.uid || mkId(),
    rowNum: index + 1,
    grade: mapping.grade || "",
    section: mapping.section || "",
    subject: mapping.subject || "",
    department_name: mapping.department_name || "",
    teacher_id: mapping.teacher_id || "",
    teacher_name: mapping.teacher_name || "",
    hod_id: mapping.hod_id || "",
    hod_name: mapping.hod_name || "",
    teachers: Array.isArray(mapping.teachers) ? mapping.teachers : [],
    hods: Array.isArray(mapping.hods) ? mapping.hods : [],
  }), []);

  useEffect(() => {
    const normalizedOwnership = normalizeOwnershipMappings(module5OwnershipMappings);
    setOwnershipRows(normalizedOwnership.map(ownershipMappingToRow));
  }, [module5OwnershipMappings, normalizeOwnershipMappings, ownershipMappingToRow]);

  const mkOwnershipUid = (grade, section, subject) =>
    `${grade}__${section}__${subject}__${Date.now()}__${Math.random().toString(36).slice(2, 7)}`;

  const addOwnershipRow = (grade = "", section = "", subject = "") => {
    if (!canEditModules1to5) return;
    setOwnershipRows((prev) => ([...prev, {
      id: mkId(),
      uid: mkOwnershipUid(grade, section, subject),
      rowNum: prev.length + 1,
      grade, section, subject,
      department_name: "",
      teacher_id: "", teacher_name: "",
      hod_id: "", hod_name: "",
      teachers: [], hods: [],
    }]));
  };

  const updateOwnershipRow = useCallback((id, field, val) => {
    if (!canEditModules1to5) return;
    setOwnershipRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: val } : r));
  }, [canEditModules1to5]);

  const deleteOwnershipRow = useCallback((id) => {
    if (!canEditModules1to5) return;
    setOwnershipRows((prev) => prev.filter((r) => r.id !== id).map((r, i) => ({ ...r, rowNum: i + 1 })));
  }, [canEditModules1to5]);

  const addRowsForAllGrades = () => {
    if (!canEditModules1to5) return;
    if (!subjectMappings.length) {
      alert("Please ensure Module 4 subjects are saved first.");
      return;
    }
    if (!module5TeacherOptions.length || !module5HodOptions.length) {
      alert("Teachers/HODs are not loaded yet. Please refresh staff and try again.");
      return;
    }
    let count = 0;
    const MAX = 10;
    const defaultTeacher = module5TeacherOptions[0];
    const defaultHod = module5HodOptions[0];
    outer: for (const grade of gradeOptions) {
      const subjects = getSubjectsForGrade(grade);
      if (!subjects.length) continue;
      for (let i = 0; i < Math.min(subjects.length, 3); i++) {
        if (count >= MAX) break outer;
        const subject = subjects[i];
        setOwnershipRows((prev) => ([...prev, {
          id: mkId(),
          uid: mkOwnershipUid(grade, "", subject),
          rowNum: prev.length + 1,
          grade, section: "", subject,
          department_name: "",
          teacher_id: defaultTeacher?.id || "",
          teacher_name: defaultTeacher?.name || "",
          hod_id: defaultHod?.id || "",
          hod_name: defaultHod?.name || "",
          teachers: defaultTeacher?.name ? [defaultTeacher.name] : [],
          hods: defaultHod?.name ? [defaultHod.name] : [],
        }]));
        count++;
      }
    }
    if (count === 0) alert("Please complete Module 4 subjects first.");
  };

  const ownershipData = useMemo(() => ownershipRows.map((r) => {
    const meta = getSubjectMeta(r.grade, r.subject);
    return { ...r, categories: meta.categories, types: meta.types };
  }), [ownershipRows, getSubjectMeta]);

  const filteredOwnership = useMemo(() => ownershipData.filter((r) =>
    (!filterGrade || r.grade === filterGrade) &&
    (!filterSection || r.section === filterSection) &&
    (!filterCategory || r.categories.includes(filterCategory)) &&
    (!filterType || r.types.includes(filterType)) &&
    (!filterSubject || r.subject === filterSubject) &&
    (!filterDepartment || r.department_name === filterDepartment) &&
    (!filterTeacher || r.teacher_name === filterTeacher) &&
    (!filterHod || r.hod_name === filterHod)
  ), [ownershipData, filterGrade, filterSection, filterCategory, filterType, filterSubject, filterDepartment, filterTeacher, filterHod]);

  const filterGrades = [...new Set(ownershipData.map((r) => r.grade).filter(Boolean))];
  const filterSections = [...new Set(ownershipData.map((r) => r.section).filter(Boolean))];
  const filterCategories = [...new Set(ownershipData.flatMap((r) => r.categories))];
  const filterTypes = [...new Set(ownershipData.flatMap((r) => r.types))];
  const filterSubjects = [...new Set(ownershipData.map((r) => r.subject).filter(Boolean))];
  const filterDepartments = [...new Set(ownershipData.map((r) => r.department_name).filter(Boolean))];
  const filterTeachers = [...new Set(ownershipData.map((r) => r.teacher_name).filter(Boolean))];
  const filterHods = [...new Set(ownershipData.map((r) => r.hod_name).filter(Boolean))];

  const gradeSectionCount = [...new Set(ownershipData.map((r) => r.grade && r.section ? `${r.grade}__${r.section}` : "").filter(Boolean))].length;
  const teacherCount = [...new Set(ownershipData.map((r) => r.teacher_name).filter(Boolean))].length;

  const localOwnershipMappings = useMemo(() => normalizeOwnershipMappings(ownershipData), [ownershipData, normalizeOwnershipMappings]);
  const savedOwnershipMappings = useMemo(() => normalizeOwnershipMappings(module5OwnershipMappings), [module5OwnershipMappings, normalizeOwnershipMappings]);

  const status = localOwnershipMappings.length === 0
    ? "Pending"
    : JSON.stringify(localOwnershipMappings) === JSON.stringify(savedOwnershipMappings)
      ? "Saved"
      : "In Progress";

  const save = async () => {
    if (!canEditModules1to5) return;
    if (!hasSelectedGrades) { alert("Please complete Module 3 first by saving grades."); return; }
    if (!ownershipRows.length) { alert("Please add at least one grade-section-subject ownership mapping."); return; }
    if (ownershipRows.find((r) => !r.grade || !r.section || !r.subject || !r.department_name.trim() || !r.teacher_id || !r.hod_id)) {
      alert("Each ownership row must include grade, section, subject, department, one teacher, and one HOD.");
      return;
    }
    const finalOwnershipMappings = localOwnershipMappings.map((row) => ({
      uid: row.uid,
      grade: row.grade,
      section: row.section,
      subject: row.subject,
      department_name: row.department_name,
      teacher_id: row.teacher_id,
      teacher_name: row.teacher_name,
      hod_id: row.hod_id,
      hod_name: row.hod_name,
      categories: row.categories,
      types: row.types,
      teachers: row.teacher_name ? [row.teacher_name] : [],
      hods: row.hod_name ? [row.hod_name] : [],
    }));
    await saveModule5AcademicOwnership({
      sectionMappings: [],
      ownershipMappings: finalOwnershipMappings,
    });
    alert("Academic Ownership Mapping saved successfully.");
  };

  const reset = async () => {
    if (!canEditModules1to5) return;
    await resetModule5AcademicOwnership();
    setOwnershipRows([]);
    setFilterGrade(""); setFilterSection(""); setFilterCategory(""); setFilterType("");
    setFilterSubject(""); setFilterDepartment(""); setFilterTeacher(""); setFilterHod("");
    setProvisionResult(null);
  };

  const provision = async () => {
    if (!canEditModules1to5) return;
    const res = await provisionModule5ToSchool();
    setProvisionResult(res);
  };

  const formatProvisionSummary = (summary) => {
    let s = `Created: ${summary?.created || 0}, Updated: ${summary?.updated || 0}, Skipped: ${summary?.skipped || 0}, Conflicts: ${summary?.conflicts || 0}`;
    if (summary?.unlinked) s += `, Unlinked: ${summary.unlinked}`;
    return s;
  };

  return (
    <ModuleCard id="module5">
      <section className="mb-6">
        <div className="text-center px-3 py-2">
          <h3 className="text-4xl font-bold text-[#D67C2C] mb-3">Academic Ownership Mapping</h3>
          <p className="text-[#1D3469] text-lg font-medium max-w-4xl mx-auto leading-[1.45]">
            Map each grade-section-subject to a teacher, HOD, and department for this active ACPP year. Classes and departments are created automatically from this mapping.
          </p>
        </div>
      </section>

      <ModuleHeader
        step="Module 5"
        title="Academic Ownership Mapping"
        description="Assign subject ownership to teachers and HODs with department mapping. Provisioning will create classes and departments automatically."
        showStatus={false}
      />

      <InfoBanner>
        Add one row per grade-section-subject combination. Each row defines which teacher and HOD owns that subject, and which department it belongs to. Provisioning creates one class per unique grade-section-subject combination and groups departments automatically.
      </InfoBanner>

      {!hasSelectedGrades ? (
        <HelpText className="text-[#b42318]">Module 3 has no saved grades for this ACPP year. Save grades first.</HelpText>
      ) : null}
      {module5LoadError ? <HelpText className="text-[#b42318]">{module5LoadError}</HelpText> : null}
      {module5StaffLoadError ? <HelpText className="text-[#b42318]">{module5StaffLoadError}</HelpText> : null}
      {module5ProvisionError ? <HelpText className="text-[#b42318]">{module5ProvisionError}</HelpText> : null}

      <SectionDivider />

      <Grid4>
        <MetricCard label="Grade-Sections Mapped" value={gradeSectionCount} meta="Unique grade-section combinations" />
        <MetricCard label="Ownership Rows" value={ownershipRows.length} meta="Total grade-section-subject rows" />
        <MetricCard label="Teachers Assigned" value={teacherCount} meta="Distinct teachers in ownership rows" />
        <MetricCard label="Status" value={status} meta="Current save state" />
      </Grid4>

      <SectionDivider />

      {canEditModules1to5 ? (
        <SoftPanel title="Grade-Section-Subject Ownership Mapping">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12px]" style={{ minWidth: 1120 }}>
              <thead>
                <tr className="bg-[#f0f6ff]">
                  <Th style={{ width: "4%" }}>#</Th>
                  <Th style={{ width: "9%" }}>Grade</Th>
                  <Th style={{ width: "9%" }}>Section</Th>
                  <Th style={{ width: "12%" }}>Subject</Th>
                  <Th style={{ width: "14%" }}>Department</Th>
                  <Th style={{ width: "14%" }}>Teacher</Th>
                  <Th style={{ width: "14%" }}>HOD</Th>
                  <Th style={{ width: "12%" }}>Category</Th>
                  <Th style={{ width: "8%" }}>Type</Th>
                  <Th style={{ width: "4%" }}>Action</Th>
                </tr>
              </thead>
              <tbody>
                {ownershipRows.length === 0 ? (
                  <tr><td colSpan={10}><EmptyState>No ownership mappings added yet. Click "Add Ownership Row" to begin.</EmptyState></td></tr>
                ) : ownershipRows.map((row) => (
                  <OwnershipRow
                    key={row.id}
                    row={row}
                    gradeOptions={gradeOptions}
                    teacherOptions={module5TeacherOptions}
                    hodOptions={module5HodOptions}
                    getSubjectsForGrade={getSubjectsForGrade}
                    getSubjectMeta={getSubjectMeta}
                    onUpdate={updateOwnershipRow}
                    onDelete={deleteOwnershipRow}
                    readOnly={!canEditModules1to5}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <BtnRow>
            <BtnPrimary onClick={() => addOwnershipRow()} disabled={module5Loading || !hasSelectedGrades || !canEditModules1to5}>Add Ownership Row</BtnPrimary>
            <BtnLight onClick={addRowsForAllGrades} disabled={module5Loading || !hasSelectedGrades || !canEditModules1to5}>Add Rows for All Grades</BtnLight>
            <BtnLight onClick={() => loadModule5AcademicOwnership()} disabled={module5Loading}>Refresh Year Data</BtnLight>
            <BtnLight onClick={() => loadModule5StaffOptions()} disabled={module5StaffLoading}>Refresh Staff Data</BtnLight>
            <BtnSecondary onClick={save} disabled={module5Loading || module5StaffLoading || !hasSelectedGrades || !canEditModules1to5}>Save Ownership Mapping</BtnSecondary>
          </BtnRow>
        </SoftPanel>
      ) : null}

      <SectionDivider />

      {canEditModules1to5 ? (
        <SoftPanel title="Create / Sync Classes & Departments">
          <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">
            Uses saved Module 5 ownership data and auto-creates one class per unique grade-section-subject combination. The assigned teacher for each row becomes the class owner. Runs upsert-safe provisioning (no data is ever deleted automatically).
          </p>
          <BtnRow>
            <BtnPrimary onClick={provision} disabled={module5ProvisionLoading || module5Loading || !canEditModules1to5}>Create/Sync Classes & Departments</BtnPrimary>
          </BtnRow>
          {provisionResult ? (
            <div className="mt-3 text-[12px] text-[#334155] leading-[1.7]">
              <div><strong>Year:</strong> {provisionResult.academic_year}</div>
              <div><strong>Classes:</strong> {formatProvisionSummary(provisionResult.classes?.summary)}</div>
              <div><strong>Departments:</strong> {formatProvisionSummary(provisionResult.departments?.summary)}</div>
              {(() => {
                const provisioned = [
                  ...(provisionResult.classes?.created || []),
                  ...(provisionResult.classes?.updated || []),
                ];
                if (!provisioned.length) return null;
                return (
                  <div className="mt-3">
                    <div className="font-semibold mb-1 text-[#1D3469]">Provisioned Classes &amp; Subjects</div>
                    <table className="w-full border-collapse text-[11px]">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-200 px-2 py-1 text-left">Grade</th>
                          <th className="border border-gray-200 px-2 py-1 text-left">Section</th>
                          <th className="border border-gray-200 px-2 py-1 text-left">Subject</th>
                          <th className="border border-gray-200 px-2 py-1 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {provisioned.map((cls, i) => {
                          const isNew = (provisionResult.classes?.created || []).some(c => c.class_id === cls.class_id);
                          return (
                            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                              <td className="border border-gray-200 px-2 py-1">{cls.grade}</td>
                              <td className="border border-gray-200 px-2 py-1">{cls.section}</td>
                              <td className="border border-gray-200 px-2 py-1">{cls.subject || <span className="text-gray-400">—</span>}</td>
                              <td className="border border-gray-200 px-2 py-1">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isNew ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                                  {isNew ? "Created" : "Updated"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          ) : null}
        </SoftPanel>
      ) : null}

      <SectionDivider />

      <SoftPanel title="Ownership Summary Table">
        <div className="grid grid-cols-2 gap-4 mb-3 md:grid-cols-4">
          <div>
            <InputLabel>Filter by Grade</InputLabel>
            <FormSelect value={filterGrade} onChange={(e) => setFilterGrade(e.target.value)}>
              <option value="">All Grades</option>
              {filterGrades.map((g) => <option key={g} value={g}>{g}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Filter by Section</InputLabel>
            <FormSelect value={filterSection} onChange={(e) => setFilterSection(e.target.value)}>
              <option value="">All Sections</option>
              {filterSections.map((s) => <option key={s} value={s}>{s}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Filter by Category</InputLabel>
            <FormSelect value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">All Categories</option>
              {filterCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Filter by Type</InputLabel>
            <FormSelect value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {filterTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Filter by Subject</InputLabel>
            <FormSelect value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)}>
              <option value="">All Subjects</option>
              {filterSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Filter by Department</InputLabel>
            <FormSelect value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}>
              <option value="">All Departments</option>
              {filterDepartments.map((d) => <option key={d} value={d}>{d}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Filter by Teacher</InputLabel>
            <FormSelect value={filterTeacher} onChange={(e) => setFilterTeacher(e.target.value)}>
              <option value="">All Teachers</option>
              {filterTeachers.map((t) => <option key={t} value={t}>{t}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Filter by HOD</InputLabel>
            <FormSelect value={filterHod} onChange={(e) => setFilterHod(e.target.value)}>
              <option value="">All HODs</option>
              {filterHods.map((h) => <option key={h} value={h}>{h}</option>)}
            </FormSelect>
          </div>
        </div>
        <BtnRow>
          <BtnLight onClick={() => { setFilterGrade(""); setFilterSection(""); setFilterCategory(""); setFilterType(""); setFilterSubject(""); setFilterDepartment(""); setFilterTeacher(""); setFilterHod(""); }}>Reset Filters</BtnLight>
        </BtnRow>
        <TableWrap>
          <thead>
            <tr>
              <Th>Grade</Th><Th>Section</Th><Th>Subject</Th><Th>Department</Th><Th>Teacher</Th><Th>HOD</Th><Th>Category</Th><Th>Type</Th>
            </tr>
          </thead>
          <tbody>
            {filteredOwnership.length === 0 ? (
              <tr><td colSpan={8}><EmptyState>No ownership rows match the current filters.</EmptyState></td></tr>
            ) : filteredOwnership.map((r, i) => (
              <tr key={i}>
                <Td>{r.grade || "—"}</Td>
                <Td>{r.section || "—"}</Td>
                <Td>{r.subject || "—"}</Td>
                <Td>{r.department_name || "—"}</Td>
                <Td>{r.teacher_name || "—"}</Td>
                <Td>{r.hod_name || "—"}</Td>
                <Td>{r.categories.map((c) => <TableChip key={c} color={c === "Academic" ? "orange" : c === "Co-Curricular" ? "green" : "purple"}>{c}</TableChip>)}</Td>
                <Td>{r.types.map((t) => <TableChip key={t}>{t}</TableChip>)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </SoftPanel>
    </ModuleCard>
  );
}
